import {
  addOrderItem,
  openOrder,
  type BuildContext,
  type OrderItemRow,
  type OrderRow,
  type SyncOp,
} from '@ims/core'

// Changes the server refused, in plain words, with a one-tap fix where there
// is a safe one (docs/plans/offline-safety.md). The fixes are pure: they turn
// the refused tx's ops into the ops that replace it (engine.resolve).

export type FixKind = 'new_order' | 'drop_serials' | 'upgrade'

export interface IssueText {
  title: string
  explain: string
}

const TEXT: Record<string, IssueText> = {
  order_closed: {
    title: 'The order was already closed',
    explain: 'Another device checked this order out before these changes reached it.',
  },
  serial_sold: {
    title: 'A serial number was already sold',
    explain:
      'Another device sold the same unit. The bill can be kept without the serial; a manager should check which sale had the unit.',
  },
  invoice_overpaid: {
    title: 'The bill was already paid',
    explain:
      'Another device recorded a payment for this bill first, so this one would pay it twice. Remove it if the money was not taken twice.',
  },
  bill_overpaid: {
    title: 'The supplier’s bill was already paid',
    explain:
      'Another device recorded a payment or a return against this bill first, so this one would cover it twice. Discard it, then pay what is still owed from the supplier’s page.',
  },
  over_allocated: {
    title: 'More was set against bills than was paid',
    explain: 'Discard this change, then record the payment again from the supplier’s page.',
  },
  bill_in_use: {
    title: 'The bill has payments against it',
    explain:
      'A bill can only be cancelled once its payments and returns are cancelled. Discard this change and cancel those first.',
  },
  limit_reached: {
    title: 'A plan limit was reached',
    explain: 'The plan’s limit was reached on another device. Upgrade the plan, then retry.',
  },
  subscription_inactive: {
    title: 'The subscription has ended',
    explain: 'Renew the subscription, then retry.',
  },
  request_decided: {
    title: 'Someone else answered this request',
    explain: 'Another device accepted or rejected the guest’s request first.',
  },
  request_changed: {
    title: 'The guest changed their request',
    explain:
      'The guest edited the round after it was accepted here. Discard, then accept it again.',
  },
  conflict: {
    title: 'The same code or number is already in use',
    explain:
      'Another device saved something with the same code first (for a product, its SKU or barcode). Discard this change, then save it again here: a product with no SKU typed gets a new one.',
  },
  immutable: {
    title: 'The document can no longer change',
    explain: 'It was finalized or sent on another device.',
  },
}

/** A refused change in plain words. */
export function issueText(code: string): IssueText {
  return (
    TEXT[code] ?? {
      title: 'The server refused this change',
      explain: 'Retry once the cause is fixed, or discard to keep the server’s version.',
    }
  )
}

const newItems = (ops: readonly SyncOp[]) =>
  ops
    .filter((o) => o.table === 'order_items' && o.op === 'upsert')
    .map((o) => o.row as unknown as OrderItemRow)
    .filter((r) => r.qty_milli > 0 && r.void_of_id === null)

/** The one-tap fix for a refused tx, if there is a safe one. */
export function fixKind(code: string, ops: readonly SyncOp[]): FixKind | null {
  if (code === 'order_closed' && newItems(ops).length > 0) return 'new_order'
  if (
    code === 'serial_sold' &&
    ops.some((o) => o.table === 'product_serials') &&
    ops.some((o) => o.table === 'sales_invoices')
  )
    return 'drop_serials'
  if (code === 'limit_reached') return 'upgrade'
  return null
}

/** The order the refused items were added to (its id), for "Move to a new order". */
export function closedOrderId(ops: readonly SyncOp[]): string | null {
  return newItems(ops)[0]?.order_id ?? null
}

/**
 * Items added to an order another device closed: a new order for the same
 * table (or a new takeaway number) with those items. Kitchen tickets are left
 * out: the items are sent to the kitchen again from the new order.
 */
export function moveItemsToNewOrder(
  ctx: BuildContext,
  ops: readonly SyncOp[],
  closed: OrderRow,
  orderNo: number | null,
): SyncOp[] {
  const { order, ops: orderOps } = openOrder(ctx, {
    tableId: closed.service_type === 'dine_in' ? closed.table_id : null,
    serviceType: closed.service_type,
    orderNo: closed.service_type === 'dine_in' ? null : orderNo,
    covers: closed.covers,
    customerId: closed.customer_id,
    label: closed.label,
  })
  const itemOps = newItems(ops).flatMap(
    (r) =>
      addOrderItem(ctx, order, {
        productId: r.product_id,
        description: r.description,
        qtyMilli: r.qty_milli,
        unitPricePaisa: r.unit_price_paisa,
        priceIncludesTax: r.price_includes_tax,
        taxRateId: r.tax_rate_id,
        taxRateBp: r.tax_rate_bp,
        note: r.note,
        station: r.station,
      }).ops,
  )
  return [...orderOps, ...itemOps]
}

/** The same sale without claiming the serial numbers another device already sold. */
export function withoutSerials(ops: readonly SyncOp[]): SyncOp[] {
  return ops.filter((o) => o.table !== 'product_serials')
}

export interface SyncErrorText {
  /** Offline and expired sign-ins sort themselves out; the rest need someone. */
  tone: 'warning' | 'danger'
  text: string
}

/**
 * A whole-sync error (SyncState.lastError, "<code>: <detail>") in words a shop
 * understands. The original stays available for support (shown as a tooltip).
 */
export function syncErrorText(lastError: string): SyncErrorText {
  const code = /^([a-z_]+): /.exec(lastError)?.[1] ?? ''
  switch (code) {
    case 'network':
      return {
        tone: 'warning',
        text: 'You’re offline or the internet is slow. Your work is saved on this device and syncs by itself when the connection is back.',
      }
    case 'unauthenticated':
      return {
        tone: 'warning',
        text: 'Your sign-in needs refreshing. It usually fixes itself; if this stays, sign out and sign in again (after your changes have synced).',
      }
    case 'subscription_inactive':
      return {
        tone: 'danger',
        text: 'The subscription has ended, so changes can’t reach the server. Renew it in Settings → Subscription.',
      }
    case 'forbidden':
      return {
        tone: 'danger',
        text: 'This account can no longer change this business. Ask the owner to check your role.',
      }
    default:
      return {
        tone: 'danger',
        text: 'Sync stopped because of a problem on our side. Your work is safe on this device. Try “Sync now”; if it keeps happening, contact support.',
      }
  }
}
